<template>
  <Modal
    container-class="!w-[95vw]"
    fullbelowsm
    :title="!templateId ? $t('projects.email_template.create') : $t('projects.email_template.edit')"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    :secondary-button-disabled="loading"
    :primary-button-loading="loading"
    :primary-button-disabled="!name || !subject || !html || loading"
    :loading="fetching"
    @primary="onSaveClick"
    @secondary="onCancelClick"
    @opened="onModalOpen"
  >
    <div class="w-full max-w-full min-h-full md:min-h-600 flex flex-col">
      <FormInput ref="name" :label="$t('projects.email_template.name')" class="mx-10 mb-3" v-model="name" :error="nameError" :readonly="loading || tags.includes('system')" />
      <PhraseEditor :items="subjectVariables" :label="$t('projects.email_template.subject_title')" class="mx-10 mb-3" v-model="subject" :error="subjectError" :readonly="loading" />

      <div class="w-full min-h-21 flex flex-col justify-start py-3 px-10 flex-shrink-0 flex-grow">
        <FormLabel :label="$t('projects.email_template.content')" class="mb-1"></FormLabel>
        <div class="flex flex-row flex-grow gap-x-4 overflow-hidden">
          <div class="w-full relative border flex-grow flex flex-col">
            <div class="flex h-5">
              <div class="text-13 cursor-pointer w-auto px-3 text-center border-r" :class="{ 'border-b': tab !== 'editor' }" @click="tab = 'editor'">{{ $t('projects.email_template.html') }}</div>
              <div class="text-13 cursor-pointer w-auto px-3 text-center border-r" :class="{ 'border-b': tab !== 'preview' }" @click="tab = 'preview'">
                {{ $t('projects.email_template.html_preview') }}
              </div>
              <div class="text-13 cursor-pointer w-auto px-3 text-center border-r" :class="{ 'border-b': tab !== 'text' }" @click="tab = 'text'">{{ $t('projects.email_template.text') }}</div>
              <div class="flex-grow border-b"></div>
              <div v-show="tab === 'editor'" class="text-13 cursor-pointer border-b border-l px-2" @click="formatEditor">{{ $t('format_code') }}</div>
            </div>
            <div class="flex-grow grid w-full">
              <div v-show="tab === 'editor'">
                <MonacoEditor v-model:value="html" multiline :options="options" width="100%" height="100%" class="flex-grow h-full" language="html" @mount="monacoMounted"></MonacoEditor>
              </div>

              <div class="w-full h-full overflow-x-hidden overflow-y-auto px-1" v-show="tab === 'preview'">
                <iframe width="100%" height="100%" class="flex-grow" style="user-select: none" :srcdoc="htmlPreviewContent" onload="onIFrameLoaded(this)"></iframe>
              </div>
              <div v-show="tab === 'text'" class="p-1 h-full">
                <Textarea ref="textarea" :readonly="loading" v-model="text" class="w-full h-full" />
              </div>
            </div>
          </div>
          <div v-if="tab === 'editor' || tab === 'text'" class="max-h-500 flex flex-col w-300 gap-y-3 select-none">
            <div class="w-full max-h-[50%] border flex flex-col">
              <div class="px-1 border-b sticky top-0 bg-stroke bg-opacity-20 text-12 font-500 py-0.5">Project Logos</div>
              <div class="overflow-auto text-13">
                <div
                  v-for="logo in logos"
                  :key="logo.name"
                  class="px-1 flex cursor-pointer"
                  :class="{ 'bg-secondary text-off-white': selectedVariable === logo.name }"
                  @click="selectedVariable = logo.name"
                  @dblclick="onLogoDblClick(logo)"
                >
                  <div class="truncate flex-grow">{{ logo.name }}</div>
                  <Icon v-if="selectedVariable === logo.name" name="plus" class="w-4 flex-shrink-0" />
                </div>
              </div>
            </div>
            <div class="flex-grow w-full border flex flex-col overflow-hidden">
              <div class="px-1 border-b sticky top-0 bg-stroke bg-opacity-20 text-12 font-500 py-0.5">Variables</div>
              <div class="overflow-auto text-13">
                <div
                  v-for="variable in variables"
                  :key="variable.name"
                  class="px-1 flex cursor-pointer"
                  :class="{ 'bg-secondary text-off-white': selectedVariable === variable.name }"
                  @click="selectedVariable = variable.name"
                  @dblclick="onVariableDblClick(variable)"
                >
                  <div class="truncate flex-grow">{{ variable.name }}</div>
                  <Icon v-if="selectedVariable === variable.name" name="plus" class="w-4 flex-shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import MonacoEditor from '@guolao/vue-monaco-editor';
import { mapActions, mapGetters, mapState } from 'vuex';
import uuidv4 from '@/helpers/uuid';

let editor;

export default {
  name: 'CreateTemplateModal',
  components: { MonacoEditor },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    templateId: {
      type: String,
    },
  },
  data() {
    return {
      completionProvider: null,
      findWidgetAction: null,
      f1Action: null,
      pasteAction: null,
      enterAction: null,
      loading: false,
      fetching: false,
      selectedVariable: 'chart1',
      name: '',
      subject: '',
      nameError: '',
      subjectError: '',
      text: '',
      textError: '',
      html: '',
      htmlError: '',
      tab: 'editor',
      editor: null,
      options: {
        formatOnType: true,
        tabSize: 2,
        formatOnPaste: true,
      },
      tags: [],
    };
  },
  beforeMount() {
    if (!window.onIFrameLoaded) {
      // Disable iframe preview click events
      window.onIFrameLoaded = (iframe) => {
        window.a = iframe;
        setTimeout(() => {
          if (iframe?.contentDocument?.body) {
            iframe.contentDocument.body.style['user-select'] = 'none';
            const links = iframe.contentDocument.body.querySelectorAll('a');
            for (let i = 0; i < links.length; i++) {
              links[i].style['pointer-events'] = 'none';
              links[i].style.cursor = 'default';
            }
          }
        }, 1000);
      };
    }
  },
  async created() {
    if (this.templateId) {
      this.fetching = true;
      const res = await this.fetchProjectTemplate({ projectId: this.projectId, templateId: this.templateId });
      if (res) {
        this.tab = 'preview';
        const { name, text, html, subject, tags } = res;
        this.name = name;
        this.subject = subject;
        this.text = text;
        this.html = html;
        this.tags = tags || [];
      }
      this.fetching = false;
    }
  },
  unmounted() {
    this.completionProvider?.dispose?.();
    this.f1Action?.dispose?.();
    this.enterAction?.dispose?.();
    this.findWidgetAction?.dispose?.();
    this.pasteAction?.dispose?.();
    this.editor?.dispose?.();
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectVariables']),
    htmlPreviewContent() {
      let { html } = this;
      this.logos.forEach((logo) => {
        html = html.replaceAll(`{${logo.name}}`, logo.url);
      });
      return html;
    },
    subjectVariables() {
      return Object.keys(this.getProjectVariables()).map((v) => ({ id: uuidv4(), label: v, value: v }));
    },
    variables() {
      const variableList = this.getProjectVariables();
      const filteredDataTypes = ['DataTable', 'Text'];
      const variableArray = Object.keys(variableList)
        .map((key) => ({ name: key, ...variableList[key] }))
        .filter((v) => filteredDataTypes.includes(v.dataType));

      const templateOnlyVariables = variableArray.filter((variable) => variable.tags && variable.tags.includes('templateonly')).sort((a, b) => a.name.localeCompare(b.name));
      const otherVariables = variableArray.filter((variable) => !variable.tags || !variable.tags.includes('templateonly')).sort((a, b) => a.name.localeCompare(b.name));

      return [...templateOnlyVariables, ...otherVariables];
    },
    logos() {
      return [
        { name: 'ProjectLogo', url: this.projectData?.[this.projectId]?.logos?.PROJECT || '' },
        { name: 'Favicon', url: this.projectData?.[this.projectId]?.logos?.FAVICON || '' },
        { name: 'NavigationLogo', url: this.projectData?.[this.projectId]?.logos?.NAVIGATION || '' },
        { name: 'Conversation', url: this.projectData?.[this.projectId]?.logos?.CONVERSATION || '' },
      ];
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectTemplate', 'createProjectTemplate', 'updateProjectTemplate']),
    async onVariableDblClick(variable) {
      if (this.tab === 'editor') {
        editor.focus();
        const selection = editor.getSelection();
        const id = { major: 1, minor: 1 };
        const op = { identifier: id, range: selection, text: `{${variable.name}}`, forceMoveMarkers: true };
        editor.executeEdits('my-source', [op]);
      } else {
        const caretPosition = this.$refs.textarea.$el.selectionStart;
        this.text = `${this.text.slice(0, caretPosition)}{${variable.name}}${this.text.slice(caretPosition)}`;
        this.$refs.textarea.$el.focus();
      }
    },
    async onLogoDblClick(logo) {
      if (this.tab === 'editor') {
        editor.focus();
        const selection = editor.getSelection();
        const id = { major: 1, minor: 1 };
        const op = { identifier: id, range: selection, text: `{${logo.name}}`, forceMoveMarkers: true };
        editor.executeEdits('my-source', [op]);
      }
    },
    onModalOpen() {
      this.$refs?.name?.$el.focus();
    },
    formatEditor() {
      editor.getAction('editor.action.formatDocument').run();
    },
    monacoMounted(_editor, monaco) {
      // validation settings
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: false,
      });
      // compiler options
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES6,
        allowNonTsExtensions: true,
      });

      editor = _editor;

      // disable `Find` widget
      // see: https://github.com/microsoft/monaco-editor/issues/287#issuecomment-328371787
      this.findWidgetAction = editor.addAction({
        id: 'findWidgetAction',
        label: 'findWidgetAction',
        // eslint-disable-next-line no-bitwise
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_F],
        run: () => {},
      }); // eslint-disable-line

      // disable `F1` command palette
      this.f1Action = editor.addAction({
        id: 'f1Action',
        label: 'f1Action',
        keybindings: [monaco.KeyCode.F1],
        run: () => {},
      }); // eslint-disable-line

      this.completionProvider = monaco.languages.registerCompletionItemProvider('html', {
        provideCompletionItems: () => {
          return {
            suggestions: [
              ...this.variables.map((item) => {
                return {
                  label: item.name,
                  insertText: item.name,
                  kind: 17,
                };
              }),
              ...this.logos.map((item) => {
                return {
                  label: item.name,
                  insertText: item.name,
                  kind: 17,
                };
              }),
            ],
          };
        },
      });
    },
    async onSaveClick() {
      this.loading = true;
      if (this.templateId) {
        const response = await this.updateProjectTemplate({ project_id: this.projectId, subject: this.subject, template_id: this.templateId, name: this.name, text: this.text, html: this.html });
        if (response) {
          this.$emit('close');
        }
      } else {
        const response = await this.createProjectTemplate({ project_id: this.projectId, subject: this.subject, name: this.name, text: this.text, html: this.html });
        if (response) {
          this.$emit('close');
        }
      }
      this.loading = false;
    },
    onCancelClick() {
      this.$emit('close');
    },
  },
};
</script>
